import { useEffect, useState } from "react";
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Spinner from "@/components/ui/loader";
import { getOrders, deleteOrder, IOrder } from "@/components/api/ordersApi";
import { toast } from "sonner";

// Generate 50 fake orders
const models = ['XC70', 'V70', 'XC90'];
const descriptions = [
    'Engine replacement and full service',
    'Brake pad replacement',
    'Oil change and filter replacement',
    'Transmission repair',
    'Suspension upgrade',
    'Full body inspection',
    'Wheel alignment and balancing',
    'AC system repair',
    'Electrical system diagnostics',
    'Exhaust system replacement',
];

// const initialOrders = Array.from({ length: 50 }, (_, i) => ({
//     id: `ORD-${String(i + 1).padStart(3, '0')}`,
//     model: models[i % models.length],
//     description: descriptions[i % descriptions.length],
//     price: `$${(Math.random() * 900 + 100).toFixed(2)}`,
//     date: new Date(2024, 0, 15 - (i % 30)).toISOString().split('T')[0],
// }));

interface OrdersTableProps {
    refreshTrigger?: number;
}

export default function OrdersTable({ refreshTrigger = 0 }: OrdersTableProps) {
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [isLoading, setLoading] = useState(true);

    const handleDelete = async (orderId: string) => {
        try {
            await deleteOrder(orderId);
            setOrders((prevOrders) => prevOrders.filter((order) => order._id !== orderId));
            toast.success("Order deleted successfully");
        } catch (error) {
            console.error("Failed to delete order", error);
            // Error is handled in api call but we can show a toast here too if needed, 
            // though handleApiThrowError might not propagate in a way that we can catch nicely if it throws a generic error.
        }
    };

    useEffect(() => {
        async function fetchOrders() {
            try {
                const ordersData = await getOrders();
                setOrders(ordersData);
                setLoading(false);
            } catch (error) {
                console.error('Failed to fetch orders in OrdersTable:', error);
                setLoading(false);
            }
        }

        fetchOrders();
    }, [refreshTrigger])

    return (
        <div className="glass-card rounded-xl overflow-hidden flex-1 flex flex-col relative">
            <div className="overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-border bg-secondary/30">
                            <th className="text-left p-4 text-sm font-medium text-muted-foreground w-1/6">Manufacturer</th>
                            <th className="text-left p-4 text-sm font-medium text-muted-foreground w-1/6">Model</th>
                            <th className="text-left p-4 text-sm font-medium text-muted-foreground w-1/6">Quantity</th>
                            <th className="text-left p-4 text-sm font-medium text-muted-foreground w-1/6">Price</th>
                            <th className="text-left p-4 text-sm font-medium text-muted-foreground w-1/6">Date</th>
                            <th className="text-right p-4 text-sm font-medium text-muted-foreground w-1/6">Actions</th>
                        </tr>
                    </thead>
                </table>
            </div>
            <ScrollArea className="flex-1">
                <table className="w-full">
                    <tbody>
                        {orders.map((order) => (
                            <tr key={order._id} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                                <td className="p-4 font-medium w-1/6">{order.manufacturer}</td>
                                <td className="p-4 font-medium w-1/6">{order.carModel}</td>
                                <td className="p-4 w-1/6">{order.quantity}</td>
                                <td className="p-4 w-1/6">{order.price}</td>
                                <td className="p-4 w-1/6">{order.createdAt}</td>
                                <td className="p-4 text-right w-1/6">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                                <MoreHorizontal className="h-4 w-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="bg-popover border-border">
                                            <DropdownMenuItem
                                                onClick={() => console.log('Update', order._id)}
                                                className="cursor-pointer"
                                            >
                                                <Pencil className="h-4 w-4 mr-2" />
                                                Update
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={() => handleDelete(order._id)}
                                                className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer"
                                            >
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </ScrollArea>
            {isLoading && (
                <Spinner loaderText="Loading orders..." />
            )}
        </div>
    );
}